import { motion } from 'framer-motion';
import NavBarItem from './NavBarItem';
import { MessageCircle, Home, Video, Bell, Settings } from 'lucide-react';

/**
 * BottomNavBar - Premium mobile navigation bar
 *
 * Features:
 * - 5 primary icons (Chats, Spaces, Feed, Notifications, Settings)
 * - Always visible at bottom of screen
 * - Safe area inset support for iPhone notch/gesture bar
 * - Badge support for unread counts
 * - Spring animations on tap
 * - Active state indicators
 *
 * @param {string} activeTab - Currently active tab
 * @param {function} onTabChange - Callback when tab is tapped
 * @param {object} unreadCounts - Unread counts for badges
 */
export default function BottomNavBar({
  activeTab = 'feed',
  onTabChange,
  unreadCounts = {
    chats: 0,
    spaces: 0,
    notifications: 0
  }
}) {
  const navItems = [
    {
      id: 'chats',
      icon: MessageCircle,
      label: 'Chats',
      badge: unreadCounts.chats
    },
    {
      id: 'spaces',
      icon: Home,
      label: 'Spaces',
      badge: unreadCounts.spaces
    },
    {
      id: 'feed',
      icon: Video,
      label: 'Feed',
      badge: 0
    },
    {
      id: 'notifications',
      icon: Bell,
      label: 'Notifs',
      badge: unreadCounts.notifications
    },
    {
      id: 'settings',
      icon: Settings,
      label: 'Settings',
      badge: 0
    }
  ];

  return (
    <motion.nav
      className="fixed bottom-0 left-0 right-0 bg-black/80 backdrop-blur-xl border-t border-white/10 z-40"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{
        type: 'spring',
        tension: 300,
        friction: 30,
        delay: 0.1
      }}
    >
      <div className="flex justify-around items-center h-16 px-2">
        {navItems.map((item) => (
          <NavBarItem
            key={item.id}
            icon={item.icon}
            label={item.label}
            isActive={activeTab === item.id}
            badge={item.badge}
            onTap={() => onTabChange(item.id)}
          />
        ))}
      </div>

      {/* Visual bottom padding for safe area (shows gradient fade) */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[env(safe-area-inset-bottom)] bg-gradient-to-t from-black/80 to-transparent pointer-events-none"
        style={{ height: 'env(safe-area-inset-bottom)' }}
      />
    </motion.nav>
  );
}
